import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type InventoryReportDateQuery = {
  fromDate?: string;
  toDate?: string;
  warehouseId?: string;
  offeringId?: string;
  pageNumber?: number;
  pageSize?: number;
};

export type InventoryMainSimilarFlowLine = {
  mainOfferingId: string;
  offeringId: string;
  relationRole: string;
  receiptQuantity: number;
  issueQuantity: number;
  transferQuantity: number;
  adjustmentQuantity: number;
  consumptionQuantity: number;
  receiptValue: number;
  issueValue: number;
  transferValue: number;
  adjustmentValue: number;
  consumptionValue: number;
  currencyCode: string;
};

export type InventoryConsignmentLine = {
  ownerPartyId: string;
  ownerPartyReference: string;
  direction: string;
  offeringId: string;
  warehouseId?: string | null;
  quantity: number;
  value: number;
  currencyCode: string;
  contractNumber: string;
};

export type InventoryConsumptionAbcLine = {
  offeringId: string;
  warehouseId?: string | null;
  abcClass: string;
  consumptionIndicator: string;
  consumptionQuantity: number;
  consumptionValue: number;
  onHandQuantity: number;
  maximumQuantity: number;
  consumptionSharePercent: number;
  currencyCode: string;
};

export type InventoryOpeningInventoryLine = {
  offeringId: string;
  warehouseId: string;
  quantity: number;
  value: number;
  currencyCode: string;
  asOfDate: string;
  fiscalYear: number;
};

export type InventoryComponentMovementLine = {
  componentOfferingId: string;
  parentOfferingId?: string | null;
  sourceType: string;
  referenceNumber: string;
  quantity: number;
  value: number;
  currencyCode: string;
  occurredAtUtc: string;
};

export type InventoryMovementTypeLine = {
  category: string;
  sourceKind: string;
  referenceNumber: string;
  offeringId: string;
  warehouseId?: string | null;
  quantity: number;
  value: number;
  currencyCode: string;
  occurredAtUtc: string;
};

export type InventoryFulfillmentBacklogLine = {
  offeringId: string;
  warehouseId: string;
  sourceReference: string;
  orderedQuantity: number;
  requestedQuantity: number;
  reservedQuantity: number;
  pickedQuantity: number;
  shippedQuantity: number;
  deliveredQuantity: number;
  pendingProcurementQuantity: number;
  statusSummary: string;
};

export type PagedReport<T> = {
  lines?: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
};

@Injectable({ providedIn: 'root' })
export class InventoryOfficialReportsApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = environment.apiBaseUrl;

  getMainSimilarFlow(query: InventoryReportDateQuery): Observable<PagedReport<InventoryMainSimilarFlowLine>> {
    return this.http.get<PagedReport<InventoryMainSimilarFlowLine>>(
      `${this.apiBase}/api/v1/inventory/reports/main-similar-flow`,
      { params: this.dateParams(query) }
    ).pipe(map(r => ({ ...r, lines: r.lines ?? [] })));
  }

  getConsignmentReport(params: { ownerPartyId?: string; direction?: string; offeringId?: string; pageNumber?: number; pageSize?: number }): Observable<PagedReport<InventoryConsignmentLine>> {
    let httpParams = new HttpParams();
    if (params.ownerPartyId) httpParams = httpParams.set('ownerPartyId', params.ownerPartyId);
    if (params.direction) httpParams = httpParams.set('direction', params.direction);
    if (params.offeringId) httpParams = httpParams.set('offeringId', params.offeringId);
    httpParams = httpParams.set('pageNumber', String(params.pageNumber ?? 1)).set('pageSize', String(params.pageSize ?? 25));
    return this.http.get<{ lines: InventoryConsignmentLine[]; pageNumber: number; pageSize: number; totalCount: number }>(
      `${this.apiBase}/api/v1/inventory/reports/consignment`, { params: httpParams }
    ).pipe(map(r => ({ lines: r.lines, pageNumber: r.pageNumber, pageSize: r.pageSize, totalCount: r.totalCount })));
  }

  getConsumptionAbc(params: { warehouseId?: string; windowDays?: number; pageNumber?: number; pageSize?: number }) {
    let httpParams = new HttpParams();
    if (params.warehouseId) httpParams = httpParams.set('warehouseId', params.warehouseId);
    if (params.windowDays) httpParams = httpParams.set('windowDays', String(params.windowDays));
    httpParams = httpParams.set('pageNumber', String(params.pageNumber ?? 1)).set('pageSize', String(params.pageSize ?? 25));
    return this.http.get<{ windowDays: number; thresholdAPercent: number; thresholdBPercent: number; lines: InventoryConsumptionAbcLine[]; pageNumber: number; pageSize: number; totalCount: number }>(
      `${this.apiBase}/api/v1/inventory/reports/consumption-abc`, { params: httpParams }
    );
  }

  getOpeningInventory(params: { fiscalYear?: number; warehouseId?: string; offeringId?: string; pageNumber?: number; pageSize?: number }) {
    let httpParams = new HttpParams();
    if (params.fiscalYear) httpParams = httpParams.set('fiscalYear', String(params.fiscalYear));
    if (params.warehouseId) httpParams = httpParams.set('warehouseId', params.warehouseId);
    if (params.offeringId) httpParams = httpParams.set('offeringId', params.offeringId);
    httpParams = httpParams.set('pageNumber', String(params.pageNumber ?? 1)).set('pageSize', String(params.pageSize ?? 25));
    return this.http.get<{ asOfDate: string; fiscalYear: number; totalQuantity: number; totalValue: number; currencyCode: string; reconciled: boolean; lines: InventoryOpeningInventoryLine[]; pageNumber: number; pageSize: number; totalCount: number }>(
      `${this.apiBase}/api/v1/inventory/reports/opening-inventory`, { params: httpParams }
    );
  }

  getComponentMovements(query: InventoryReportDateQuery & { parentOfferingId?: string }) {
    let params = this.dateParams(query);
    if (query.parentOfferingId) params = params.set('parentOfferingId', query.parentOfferingId);
    return this.http.get<PagedReport<InventoryComponentMovementLine>>(
      `${this.apiBase}/api/v1/inventory/reports/component-movements`, { params }
    ).pipe(map(r => ({ ...r, lines: r.lines ?? [] })));
  }

  getMovementTypes(query: InventoryReportDateQuery) {
    return this.http.get<{ fromDate: string; toDate: string; ledgerQuantityTotal: number; ledgerValueTotal: number; reportQuantityTotal: number; reportValueTotal: number; reconciled: boolean; lines: InventoryMovementTypeLine[]; pageNumber: number; pageSize: number; totalCount: number }>(
      `${this.apiBase}/api/v1/inventory/reports/movement-types`, { params: this.dateParams(query) }
    );
  }

  getFulfillmentBacklog(params: { warehouseId?: string; offeringId?: string; pageNumber?: number; pageSize?: number }) {
    let httpParams = new HttpParams();
    if (params.warehouseId) httpParams = httpParams.set('warehouseId', params.warehouseId);
    if (params.offeringId) httpParams = httpParams.set('offeringId', params.offeringId);
    httpParams = httpParams.set('pageNumber', String(params.pageNumber ?? 1)).set('pageSize', String(params.pageSize ?? 25));
    return this.http.get<{ lines: InventoryFulfillmentBacklogLine[]; pageNumber: number; pageSize: number; totalCount: number }>(
      `${this.apiBase}/api/v1/inventory/reports/fulfillment-backlog`, { params: httpParams }
    );
  }

  exportReport(path: string, params: HttpParams): Observable<Blob> {
    return this.http.get(`${this.apiBase}${path}`, { params, responseType: 'blob' });
  }

  private dateParams(query: InventoryReportDateQuery): HttpParams {
    let params = new HttpParams()
      .set('pageNumber', String(query.pageNumber ?? 1))
      .set('pageSize', String(query.pageSize ?? 25));
    if (query.fromDate) params = params.set('fromDate', query.fromDate);
    if (query.toDate) params = params.set('toDate', query.toDate);
    if (query.warehouseId) params = params.set('warehouseId', query.warehouseId);
    if (query.offeringId) params = params.set('offeringId', query.offeringId);
    return params;
  }
}
